import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Mail,
  Plus,
  RefreshCw,
  Copy,
  Archive,
  Eye,
} from "react-feather";
import { SiMicrosoftteams, SiWhatsapp } from "react-icons/si";
import { toast } from "react-toastify";
import {
  archiveTemplateAPI,
  cloneTemplateAPI,
  getTemplateLibraryAPI,
} from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import DataTableWrapper from "../../../Components/shared/DataTableWrapper";
import ConfirmModal from "../../../Components/shared/ConfirmModal";
import { platformUrl } from "../../../utils/mhNav";
import { applyTemplateSampleVars } from "../../../mockData/platform/templates";
import styles from "./TemplateLibrary.module.scss";

// ── Channel icon ──────────────────────────────────────────────────────────────
function channelIcon(ch) {
  if (ch === "teams") return <SiMicrosoftteams size={14} />;
  if (ch === "whatsapp") return <SiWhatsapp size={14} />;
  return <Mail size={14} />;
}

// ── Channel badge ─────────────────────────────────────────────────────────────
function ChannelBadge({ channel }) {
  const map = {
    teams: styles.channelTeams,
    whatsapp: styles.channelWhatsapp,
    email: styles.channelEmail,
  };
  return (
    <span
      className={`${styles.channelBadge} ${map[channel] || styles.channelEmail}`}
    >
      {channelIcon(channel)}
      <span>{channel}</span>
    </span>
  );
}

// ── Status pill ───────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const map = {
    active: styles.pillActive,
    draft: styles.pillDraft,
    archived: styles.pillArchived,
  };
  return (
    <span className={`${styles.pill} ${map[status] || styles.pillDraft}`}>
      {status}
    </span>
  );
}

// ── Var highlighter ───────────────────────────────────────────────────────────
function highlightVars(text) {
  if (!text) return null;
  return String(text)
    .split(/(\{\{[\w_]+\}\})/g)
    .map((p, i) =>
      /^\{\{[\w_]+\}\}$/.test(p) ? (
        <span key={i} className={styles.var}>
          {p}
        </span>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
}

// ── Filter select ─────────────────────────────────────────────────────────────
function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className={styles.filterGroup}>
      <label>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([val, text]) => (
          <option key={val} value={val}>
            {text}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Preview panel tabs ────────────────────────────────────────────────────────
const PREVIEW_TABS = ["teams", "whatsapp", "email"];

// ─────────────────────────────────────────────────────────────────────────────
export default function TemplateLibrary() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [channel, setChannel] = useState("all");
  const [useCase, setUseCase] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);
  const [archiveId, setArchiveId] = useState(null);
  const [previewTab, setPreviewTab] = useState("teams");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTemplateLibraryAPI({
        channel,
        useCase,
        status,
        search,
      });
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [channel, useCase, status, search]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        name: "Name",
        selector: (r) => r.name,
        sortable: true,
        grow: 2,
        cell: (r) => (
          <span style={{ fontWeight: 600, color: "#1a1525" }}>{r.name}</span>
        ),
      },
      {
        name: "Channel",
        selector: (r) => r.channel,
        cell: (r) => <ChannelBadge channel={r.channel} />,
      },
      { name: "Use case", selector: (r) => r.useCase, sortable: true },
      {
        name: "Version",
        selector: (r) => r.version,
        sortable: true,
        width: "80px",
        cell: (r) => (
          <span style={{ color: "#a099b0", fontSize: "0.8rem" }}>
            v{r.version}
          </span>
        ),
      },
      {
        name: "Status",
        selector: (r) => r.status,
        cell: (r) => <StatusPill status={r.status} />,
      },
      {
        name: "Last updated",
        selector: (r) => new Date(r.updatedAt).toLocaleString(),
        sortable: true,
        cell: (r) => (
          <span style={{ fontSize: "0.78rem", color: "#a099b0" }}>
            {new Date(r.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        name: "Actions",
        ignoreRowClick: true,
        width: "180px",
        cell: (r) => (
          <div className={styles.actionBtns}>
            <Link
              to={platformUrl(`templates/${r.id}`)}
              className={styles.btnIcon}
              title="Edit"
            >
              Edit
            </Link>
            <button
              className={styles.btnIcon}
              title="Clone"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await cloneTemplateAPI(r.id);
                  toast.success("Cloned");
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              <Copy size={13} />
            </button>
            <button
              className={`${styles.btnIcon} ${styles.btnIconDanger}`}
              title="Archive"
              disabled={r.status === "archived"}
              onClick={(e) => {
                e.stopPropagation();
                setArchiveId(r.id);
              }}
            >
              <Archive size={13} />
            </button>
            <button
              className={`${styles.btnIcon} ${styles.btnIconInfo}`}
              title="Preview"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewRow(r);
                setPreviewTab(
                  r.channel === "email"
                    ? "email"
                    : r.channel === "whatsapp"
                      ? "whatsapp"
                      : "teams",
                );
                setPreviewOpen(true);
              }}
            >
              <Eye size={13} />
            </button>
          </div>
        ),
      },
    ],
    [load],
  );

  const previewBody = previewRow
    ? applyTemplateSampleVars(previewRow.bodyMarkdown)
    : "";
  const previewSubject = previewRow
    ? applyTemplateSampleVars(previewRow.subject || "")
    : "";

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading && !rows.length) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Template library"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Templates" },
          ]}
        />
        <div className={styles.skeletonGrid}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={styles.skeletonCard}>
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className={styles.skeletonRow} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error && !rows.length) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Template library"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Templates" },
          ]}
        />
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <AlertTriangle size={22} />
          </div>
          <h3>Could not load templates</h3>
          <p>{error.message}</p>
          <button className={styles.btnPrimary} onClick={load}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Main ───────────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <PageHeader
        title="Template library"
        breadcrumbs={[
          { label: "Platform", path: platformUrl("dashboard") },
          { label: "Templates" },
        ]}
      />

      {/* ── Filter bar ── */}
      <div className={styles.filterBar}>
        <FilterSelect
          label="Channel"
          value={channel}
          onChange={setChannel}
          options={[
            ["all", "All channels"],
            ["teams", "Teams"],
            ["whatsapp", "WhatsApp"],
            ["email", "Email"],
          ]}
        />
        <FilterSelect
          label="Use case"
          value={useCase}
          onChange={setUseCase}
          options={[
            ["all", "All use cases"],
            ["Booking Confirmation", "Booking Confirmation"],
            ["Reminder", "Reminder"],
            ["MOM Ready", "MOM Ready"],
            ["Cancellation", "Cancellation"],
          ]}
        />
        <FilterSelect
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            ["all", "All"],
            ["active", "Active"],
            ["draft", "Draft"],
            ["archived", "Archived"],
          ]}
        />
        <div className={`${styles.filterGroup} ${styles.filterSearch}`}>
          <label>Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Template name…"
          />
        </div>
        <button
          className={styles.btnPrimary}
          style={{ marginTop: "auto" }}
          onClick={() => navigate(platformUrl("templates/new"))}
        >
          <Plus size={14} /> New template
        </button>
      </div>

      {/* ── Layout: table + side preview ── */}
      <div className={styles.layout}>
        <div className={styles.main}>
          <div className={styles.tableCard}>
            <div className={styles.tableCardHeader}>
              <h6>Templates</h6>
              <span className={styles.tableCount}>{rows.length} templates</span>
            </div>
            <DataTableWrapper
              columns={columns}
              data={rows}
              loading={false}
              keyField="id"
              onRowClick={(r) => setSelected(r)}
              showSearch={false}
            />
          </div>
        </div>

        {/* ── Side preview panel ── */}
        {selected && (
          <aside className={styles.preview}>
            <div className={styles.previewHeader}>
              <span className={styles.previewTitle}>{selected.name}</span>
              <button
                className={styles.closeBtn}
                onClick={() => setSelected(null)}
              >
                ✕
              </button>
            </div>

            <div className={styles.previewTabs}>
              {PREVIEW_TABS.map((t) => (
                <button
                  key={t}
                  className={`${styles.previewTab} ${previewTab === t ? styles.previewTabActive : ""}`}
                  onClick={() => setPreviewTab(t)}
                >
                  {channelIcon(t)} {t}
                </button>
              ))}
            </div>

            {previewTab === "teams" && (
              <div className={styles.previewCard}>
                <div className={styles.previewCardLabel}>Adaptive card</div>
                <div className={styles.previewBody}>
                  {highlightVars(
                    applyTemplateSampleVars(selected.bodyMarkdown),
                  )}
                </div>
                <button
                  className={styles.btnPrimary}
                  style={{ fontSize: "0.75rem", padding: "5px 12px" }}
                >
                  Open meeting
                </button>
              </div>
            )}

            {previewTab === "whatsapp" && (
              <div className={styles.phoneFrame}>
                <div className={styles.phoneBar}>WhatsApp</div>
                <div className={styles.phoneBubbleWrap}>
                  <div className={styles.phoneBubble}>
                    {highlightVars(
                      applyTemplateSampleVars(selected.bodyMarkdown),
                    )}
                  </div>
                </div>
              </div>
            )}

            {previewTab === "email" && (
              <div className={styles.previewCard}>
                <div className={styles.previewCardLabel}>Subject</div>
                <div className={styles.previewSubject}>
                  {highlightVars(
                    applyTemplateSampleVars(selected.subject || selected.name),
                  )}
                </div>
                <hr
                  style={{
                    borderColor: "rgba(100,73,152,0.1)",
                    margin: "10px 0",
                  }}
                />
                <div className={styles.previewBody}>
                  {highlightVars(
                    applyTemplateSampleVars(selected.bodyMarkdown),
                  )}
                </div>
              </div>
            )}
          </aside>
        )}
      </div>

      {/* ── Full preview modal ── */}
      {previewOpen && (
        <div
          className={styles.modalOverlay}
          onClick={() => setPreviewOpen(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span>Preview — {previewRow?.name}</span>
              <button
                className={styles.closeBtn}
                onClick={() => setPreviewOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <div
                className={styles.previewTabs}
                style={{ marginBottom: "1rem" }}
              >
                {PREVIEW_TABS.map((t) => (
                  <button
                    key={t}
                    className={`${styles.previewTab} ${previewTab === t ? styles.previewTabActive : ""}`}
                    onClick={() => setPreviewTab(t)}
                  >
                    {channelIcon(t)} {t}
                  </button>
                ))}
              </div>
              {previewTab === "email" && (
                <p className={styles.modalSubject}>
                  <strong>Subject:</strong> {highlightVars(previewSubject)}
                </p>
              )}
              <div className={styles.previewCard}>
                {highlightVars(previewBody)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Archive confirm ── */}
      <ConfirmModal
        isOpen={Boolean(archiveId)}
        onClose={() => setArchiveId(null)}
        onConfirm={async () => {
          try {
            await archiveTemplateAPI(archiveId);
            toast.success("Archived");
            setArchiveId(null);
            load();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Archive template?"
        message="Archived templates are hidden from active channels."
        confirmLabel="Archive"
        danger
      />
    </div>
  );
}
